import { DateTime } from 'luxon';
import axios from 'smg/axios';
import { Images } from 'smg/customer-api/images';
import * as models from 'smg/customer-api/models';
import { CustomerAPIV2Root } from 'smg/customer-api/v2';
import { DownloadURLsZipOrSingle } from 'smg/download';
import { ShowError } from 'smg/errors';
import { WebSafeMime } from 'smg/mime';
import { IsPdfLike, Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import i18n from '../../i18n';

void main();

const pageSize = 24;
const uploadedByYou = 'Uploaded by you';

function kindToken(kind: string): string {
    switch (kind) {
    case 'up':
        return 'type:upload';
    case 'us':
        return 'type:image';
    case 'ai':
        return 'tag:ai-gen';
    default:
        return '';
    }
}

// Extension first: stored mimes lie about container formats (.ai files carry
// application/pdf), and customers think in extensions anyway.
function fileFormat(name: string, mime: string): string {
    const [base, rawExt, found] = strings.CutLast(name, '.');
    const ext = rawExt.toUpperCase();
    if (found && base && /^[A-Z0-9]{1,5}$/.test(ext)) {
        if (ext === 'JPG') {
            return 'JPEG';
        }
        // "AI" reads as artificial intelligence everywhere now — and this
        // page already has an AI chip for generated art.
        if (ext === 'AI') {
            return 'Illustrator';
        }
        return ext;
    }

    switch (mime) {
    case 'image/jpeg':
        return 'JPEG';
    case 'image/png':
        return 'PNG';
    case 'application/pdf':
        return 'PDF';
    case 'application/postscript':
        return 'Illustrator';
    default: {
        const [, subtype, ok] = strings.Cut(mime, '/');
        return (ok ? subtype : mime).toUpperCase();
    }
    }
}

function formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (bytes <= 0) {
        return '';
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(created: DateTime): string {
    if (!created.isValid) {
        return '';
    }
    return created.toLocaleString(DateTime.DATE_MED);
}

function currentKind($kind: HTMLSelectElement | null): string {
    return $kind?.value || 'all';
}

// The page is always in exactly one of these states; render() is the single
// place that maps a state onto what's visible.
type libraryState = { kind: 'loading' } |
    { kind: 'results'; pager: boolean } |
    { kind: 'empty' } |
    { kind: 'noMatches' } |
    { kind: 'error' };

let currentState: libraryState = { kind: 'loading' };

function render(state: libraryState) {
    currentState = state;
    const show = (selector: string, visible: boolean) => {
        document.querySelector(selector)?.classList.toggle('d-none', !visible);
    };
    show('#files-container', state.kind === 'loading' || state.kind === 'results');
    show('#files-pager', state.kind === 'results' && state.pager);
    show('#empty-container', state.kind === 'empty');
    show('#no-matches-container', state.kind === 'noMatches');
    show('#error-container', state.kind === 'error');
    // A brand-new empty library hides the search/filter/sort controls; every
    // other state keeps them.
    show('.search-container', state.kind !== 'empty');
}

function hiddenCard(): HTMLElement {
    const $div = document.createElement('div');
    $div.classList.add('d-none');
    return $div;
}

// What a card shows, whether the file came from the API or was uploaded on
// this page moments ago.
interface fileFacts {
    name: string;
    mime: string;
    size: number;
    created: DateTime;
    // What the download button fetches and fancybox opens.
    originalURL: string;
    // What the thumbnail renders; '' means the format placeholder instead.
    previewURL: string;
    previewMime: string;
    aiGenerated: boolean;
    // Set for uploads: they can start a quote.
    upload?: { uploadID: number; name: string; url: string; mime: string };
}

function fileFactsFromRow(row: models.ImageOrUpload): fileFacts | undefined {
    const upload = row.upload?.id ? row.upload : undefined;
    const image = row.image?.id ? row.image : undefined;

    if (upload) {
        const url = upload.url();
        return {
            name: upload.name,
            mime: upload.mime,
            size: upload.size,
            created: upload.created,
            originalURL: url,
            previewURL: row.convertible ? url : '',
            previewMime: upload.mime,
            aiGenerated: upload.tags.includes('ai-gen'),
            upload: { uploadID: upload.id, name: upload.name, url, mime: upload.mime },
        };
    }
    if (image) {
        // Either rendition can be absent — fall back so an original-only image
        // still previews and a raster-only one still has metadata/downloads.
        const primary = image.files.original.file ? image.files.original : image.files.raster;
        const urls = image.getURLs();
        const previewURL = image.url();
        return {
            name: image.name,
            mime: primary.mime,
            size: primary.size,
            created: image.created,
            originalURL: urls.original || urls.raster,
            previewURL: row.convertible ? previewURL : '',
            previewMime: image.files.raster.mime || image.files.original.mime,
            aiGenerated: false,
        };
    }
    return undefined;
}

function drawCard(facts: fileFacts): HTMLElement {
    const { name, mime, originalURL, previewURL, previewMime } = facts;
    const format = fileFormat(name, mime);

    const $cardEl = template.Clone('#file-card-template').firstElementChild as HTMLElement;

    const $name = $cardEl.querySelector('.file-name') as HTMLElement;
    $name.textContent = name;
    $name.title = name;

    const $sub = $cardEl.querySelector('.file-sub') as HTMLElement;
    const $subFormat = document.createElement('b');
    $subFormat.textContent = format;
    const subRest = [formatSize(facts.size), formatDate(facts.created)].filter(Boolean).join(' · ');
    $sub.replaceChildren($subFormat, document.createTextNode(subRest ? ` · ${subRest}` : ''));

    const $kindChip = $cardEl.querySelector('.file-chip-kind') as HTMLElement;
    $kindChip.textContent = facts.upload ? uploadedByYou : 'Prepared by us';
    $kindChip.classList.add(facts.upload ? 'tag-fill-white' : 'tag-fill-primary');

    const $aiChip = $cardEl.querySelector('.file-chip-ai') as HTMLElement;
    if (facts.aiGenerated) {
        $aiChip.classList.remove('d-none');
        $aiChip.classList.add('gap-1');
    }

    const $preview = $cardEl.querySelector('.file-preview') as HTMLAnchorElement;
    const $img = $cardEl.querySelector('.file-preview-img') as HTMLImageElement;
    const $none = $cardEl.querySelector('.file-preview-none') as HTMLElement;
    const $format = $cardEl.querySelector('.file-preview-format') as HTMLElement;

    if (previewMime === 'image/png' || previewMime === 'image/gif') {
        $preview.classList.add('file-preview-trans');
    }

    if (previewURL) {
        $img.alt = name;
        $preview.href = previewURL;
        $preview.setAttribute('data-fancybox', 'account-files');
        $preview.setAttribute('data-caption', name);

        void Picture($img, previewURL, {
            displayWidth: 384,
            sizes: '(min-width: 992px) 25vw, (min-width: 768px) 33vw, 50vw',
            fancyboxThumb: true,
            alt: name,
            mime,
            pdfSrc: IsPdfLike(mime, name, originalURL) ? originalURL : undefined,
        });
    } else {
        $img.classList.add('d-none');
        $none.classList.remove('d-none');
        $none.classList.add('d-flex');
        $format.textContent = format;
        $preview.removeAttribute('href');
        $preview.classList.add('pe-none');
    }

    const $download = $cardEl.querySelector('.file-download-btn') as HTMLButtonElement;
    $download.addEventListener('click', () => {
        void DownloadURLsZipOrSingle(name, [{ name, url: originalURL }]).catch(ShowError);
    });

    if (facts.upload) {
        const file = facts.upload;
        const $quote = $cardEl.querySelector('.file-new-quote-btn') as HTMLButtonElement;
        $quote.classList.remove('d-none');
        $quote.addEventListener('click', () => {
            void openNewQuote(file);
        });
    }

    return $cardEl;
}

function drawFileCard(row: models.ImageOrUpload): HTMLElement {
    const facts = fileFactsFromRow(row);
    return facts ? drawCard(facts) : hiddenCard();
}

// A file chosen on this page: what the browser knows about it before any
// request is made, plus the upload id once POST /uploads has reserved one.
interface localFile {
    name: string;
    mime: string;
    size: number;
    blobURL: string;
    $card: HTMLElement;
    uploadID: number;
}

// A file still transferring takes its place in the grid right away, drawn on
// the same card as its finished neighbors: the browser's own preview where it
// can render one, dimmed under a spinner, and nothing to click yet.
function drawUploadingCard(file: localFile): HTMLElement {
    const $cardEl = template.Clone('#file-card-template').firstElementChild as HTMLElement;
    ($cardEl.querySelector('.file-card') as HTMLElement).classList.add('file-card-uploading');

    const $name = $cardEl.querySelector('.file-name') as HTMLElement;
    $name.textContent = file.name;
    $name.title = file.name;

    const $sub = $cardEl.querySelector('.file-sub') as HTMLElement;
    $sub.textContent = i18n('accountFilesUploading');

    const $kindChip = $cardEl.querySelector('.file-chip-kind') as HTMLElement;
    $kindChip.textContent = uploadedByYou;
    $kindChip.classList.add('tag-fill-white');

    const $preview = $cardEl.querySelector('.file-preview') as HTMLAnchorElement;
    $preview.removeAttribute('href');
    $preview.classList.add('pe-none');

    const $img = $cardEl.querySelector('.file-preview-img') as HTMLImageElement;
    if (WebSafeMime(file.mime)) {
        $img.src = file.blobURL;
        $img.alt = file.name;
        if (file.mime === 'image/png' || file.mime === 'image/gif') {
            $preview.classList.add('file-preview-trans');
        }
    } else {
        $img.classList.add('d-none');
        const $none = $cardEl.querySelector('.file-preview-none') as HTMLElement;
        $none.classList.remove('d-none');
        $none.classList.add('d-flex');
        ($cardEl.querySelector('.file-preview-format') as HTMLElement).textContent = fileFormat(file.name, file.mime);
    }

    ($cardEl.querySelector('.file-preview-spinner') as HTMLElement).classList.remove('d-none');
    ($cardEl.querySelector('.file-actions') as HTMLElement).classList.add('invisible');

    return $cardEl;
}

// The finished card for a file kept moments ago, built from what this page
// already knows plus the kept URL: the preview is the browser's own render of
// the local bytes (Picture handles blob URLs, PDFs included), so the card is
// complete the instant keep returns and no read has to find the row.
function keptFileFacts(file: localFile, kept: { url: string }): fileFacts {
    const previewable = WebSafeMime(file.mime) || IsPdfLike(file.mime, file.name, kept.url);
    return {
        name: file.name,
        mime: file.mime,
        size: file.size,
        created: DateTime.now(),
        originalURL: kept.url,
        previewURL: previewable ? file.blobURL : '',
        previewMime: file.mime,
        aiGenerated: false,
        upload: { uploadID: file.uploadID, name: file.name, url: kept.url, mime: file.mime },
    };
}

// POST /uploads only reserves the row; a file joins the library when it is
// kept, which is what stamps its Key and makes GET /images list it.
async function keepUploads(uploadIDs: number[]): Promise<models.KeepUploadsResponse> {
    const resp = await axios.patch('/uploads/keep', new models.KeepUploadsRequest({ uploadIDs }), { baseURL: CustomerAPIV2Root() });
    return models.KeepUploadsResponse.fromJSON(resp.data);
}

// Cards for files kept by this page whose rows the next grid load may not
// return yet: the load is a separate request, and in production it reads a
// replica that can trail the keep by a moment. After that load, any of these
// the rows did not include goes back on top, so a file never appears, vanishes,
// and reappears on the next visit.
const freshUploads = new Map<number, HTMLElement>();

function restoreFreshUploads(rows: models.ImageOrUpload[], $grid: HTMLElement): number {
    const listed = new Set(rows.map(row => row.upload?.id ?? 0));
    let restored = 0;
    for (const [uploadID, $card] of [...freshUploads].reverse()) {
        if (!listed.has(uploadID)) {
            $grid.prepend($card);
            restored++;
        }
    }
    freshUploads.clear();
    return restored;
}

interface uploadTargets {
    pl: PageLoader<models.ImageOrUpload>;
    $grid: HTMLElement;
    // Puts the library on the view where a new upload belongs: page 0, newest
    // first, no search term, no filter that hides uploads. Reports whether
    // anything had to change, which is what decides if the grid reloads.
    showNewest: () => boolean;
}

// The toolbar button and the page itself are one uploader: picked and dropped
// files go straight into the library, no form and no second step. A batch is
// every file selected until the last of them settles, so ten dropped files
// mean one keep call and at most one reload, not ten.
function initUploads({ pl, $grid, showNewest }: uploadTargets) {
    const $label = document.querySelector<HTMLLabelElement>('#files-upload-btn');
    const $page = document.querySelector<HTMLElement>('#files-page');
    const $input = $label?.querySelector('input');
    if (!$label || !$page || !$input) {
        return;
    }

    let selected = 0;
    let settled = 0;
    let completed: localFile[] = [];
    let $cards: HTMLElement[] = [];

    const finishBatch = () => {
        if (settled < selected) {
            return;
        }
        const files = completed;
        const $batchCards = $cards;
        selected = 0;
        settled = 0;
        completed = [];
        $cards = [];
        uploader.clear();

        if (!files.length) {
            // Every file failed; the uploader already said so. Reload so the
            // page returns to whatever state the failed cards displaced.
            pl.load();
            return;
        }

        void keepUploads(files.map(file => file.uploadID)).then(kept => {
            for (const file of files) {
                const keptFile = kept.files.find(f => f.uploadID === file.uploadID);
                if (!keptFile) {
                    file.$card.remove();
                    continue;
                }
                const $card = drawCard(keptFileFacts(file, keptFile));
                file.$card.replaceWith($card);
                freshUploads.set(file.uploadID, $card);
            }

            // On the newest-first view the finished cards already sit where
            // the library will list them, so nothing needs reloading.
            if (showNewest()) {
                pl.load();
            } else {
                freshUploads.clear();
            }

            if (files.length === 1) {
                toast.Success(i18n('accountFilesUploadedOne'));
            } else {
                toast.Success(i18n('accountFilesUploadedMany').replace('%s', String(files.length)));
            }
        }).catch(err => {
            ShowError(err);
            for (const $card of $batchCards) {
                $card.remove();
            }
            pl.load();
        });
    };

    // No drop zones of its own: the document handler below feeds the input,
    // gated on file drags, where the Uploader's zone handler would swallow
    // every drag over the page (text into the search box included).
    const uploader = new uploads.Uploader($label, {
        thumbnails: false,
        dropZones: [],
        handlers: () => {
            let file: localFile | undefined;
            const handlerFuncs: uploads.HandlerFuncs = {
                onSelected: (blobURL, name, mime) => {
                    selected++;
                    // The input still holds the selection here; the uploader
                    // clears it after it has queued the POST.
                    const size = Array.from($input.files ?? []).find(f => f.name === name)?.size ?? 0;
                    file = { name, mime: mime ?? '', size, blobURL, uploadID: 0, $card: hiddenCard() };
                    file.$card = drawUploadingCard(file);
                    $cards.push(file.$card);
                    $grid.prepend(file.$card);
                    if (currentState.kind !== 'results') {
                        render({ kind: 'results', pager: false });
                    }
                },
                onUploadCompleted: uploadID => {
                    if (file) {
                        file.uploadID = uploadID;
                        completed.push(file);
                    }
                    settled++;
                    finishBatch();
                },
                onUploadFailed: () => {
                    file?.$card.remove();
                    settled++;
                    finishBatch();
                },
            };
            return handlerFuncs;
        },
    });

    // The whole document is the drop target; the cue is a class on the page
    // that fades the tint and hint in. dragover is the one signal a drag is
    // still in progress: the browser fires it continuously while a file is
    // over the page, cursor moving or not, and it stops the moment the drag
    // leaves the window, is cancelled, or is dropped. Counting dragenter
    // against dragleave is not reliable (a node the cursor is over can be
    // replaced mid-drag, as thumbnails loading into <picture> do, and Firefox
    // fires mousemove during a drag), so the cue simply outlives the last
    // dragover by a beat: a still cursor gets one every 350ms give or take
    // 200, so the beat is longer than that. It only starts while no modal is
    // open: the quote form has its own dropzone and keeps its drags.
    const isFileDrag = (e: DragEvent): boolean => e.dataTransfer?.types.includes('Files') ?? false;
    const dragSettleMs = 800;
    let dragTimer = 0;
    const endDrag = () => {
        window.clearTimeout(dragTimer);
        dragTimer = 0;
        $page.classList.remove('files-dropping');
    };
    const holdDrag = (e: DragEvent) => {
        if (!isFileDrag(e) || (dragTimer === 0 && document.querySelector('.modal.show'))) {
            return;
        }
        e.preventDefault();
        $page.classList.add('files-dropping');
        window.clearTimeout(dragTimer);
        dragTimer = window.setTimeout(endDrag, dragSettleMs);
    };

    document.addEventListener('dragenter', holdDrag);
    document.addEventListener('dragover', holdDrag);
    document.addEventListener('drop', e => {
        if (dragTimer === 0 || !isFileDrag(e)) {
            return;
        }
        e.preventDefault();
        endDrag();
        if (e.dataTransfer) {
            $input.files = e.dataTransfer.files;
            $input.dispatchEvent(new Event('change'));
        }
    });
}

// A file has no product context, so the quote form itself shows a required
// product dropdown (productSelect). quote.ts drags a heavy dependency graph, so
// it loads on click, not up front.
async function openNewQuote(file: { uploadID: number; name: string; url: string; mime: string }) {
    const quote = await import('../../quote');
    quote.QuoteModal({
        lineItem: '',
        type: '',
        size: '',
        productSelect: quote.AccountQuoteProducts,
        prefixNotes: `I'd like a new quote using this file from my account: ${file.name}`,
        attachUploads: [file],
        form: 'account files',
        backButtonText: 'Back to Files',
    });
}

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const $target = document.querySelector('#files-container') as HTMLElement | null;
    if (!$target) {
        return;
    }

    const $kind = document.querySelector('#files-kind') as HTMLSelectElement | null;
    const kindParam = QueryString().get('kind')?.trim() || 'all';
    if ($kind) {
        $kind.value = kindParam;
        if ($kind.selectedIndex === -1) {
            $kind.value = 'all';
            QueryString().delete('kind');
        }
    }

    const pl: PageLoader<models.ImageOrUpload> = new PageLoader<models.ImageOrUpload>({
        clearResults: true,
        parent: $target,
        pageSize,
        beforeLoad: () => {
            render({ kind: 'loading' });
        },
        get: () => Images({
            q: [kindToken(currentKind($kind)), pl.search()].filter(Boolean).join(' ') || undefined,
            sort: pl.sort() || 'created desc',
            max: pageSize,
            p: pl.currentPage(),
        }).then(resp => ({
            rows: resp.results,
            nextPage: resp.nextPage,
        })),
        draw: drawFileCard,
        afterLoad: (rows, nextPage) => {
            const restored = restoreFreshUploads(rows, $target);
            if (rows.length + restored > 0) {
                // A single full page needs no pager — show it only when there's
                // somewhere to go.
                render({ kind: 'results', pager: nextPage || pl.currentPage() > 0 });
                return;
            }

            const emptyLibrary = !pl.search() && currentKind($kind) === 'all' && pl.currentPage() === 0;
            render({ kind: emptyLibrary ? 'empty' : 'noMatches' });
        },
        onError: err => {
            ShowError(err);
            render({ kind: 'error' });
        },
    });

    $kind?.addEventListener('change', () => {
        setKind($kind.value || 'all');
        pl.setPage(0);
        pl.load();
    });

    const clearSearch = () => {
        const $input = document.querySelector('#account-search-input') as HTMLInputElement | null;
        if ($input) {
            $input.value = '';
        }
        QueryString().delete('q');
    };
    const setKind = (kind: string) => {
        if ($kind) {
            $kind.value = kind;
        }
        if (kind !== 'all') {
            QueryString().set('kind', kind);
        } else {
            QueryString().delete('kind');
        }
    };

    document.querySelector('#clear-search-btn')?.addEventListener('click', () => {
        clearSearch();
        setKind('all');
        pl.setPage(0);
        pl.load();
    });

    document.querySelector('#retry-btn')?.addEventListener('click', () => {
        pl.load();
    });

    initUploads({
        pl,
        $grid: $target,
        showNewest: () => {
            const $sort = document.querySelector<HTMLSelectElement>('#sort');
            // "Uploaded by you" still lists the new file; the other two
            // filters would hide it.
            const kindHides = currentKind($kind) !== 'all' && currentKind($kind) !== 'up';
            const changed = pl.search() !== '' || kindHides || ($sort?.value ?? 'created desc') !== 'created desc' || pl.currentPage() !== 0;
            if (!changed) {
                return false;
            }

            clearSearch();
            if (kindHides) {
                setKind('all');
            }
            if ($sort) {
                $sort.value = 'created desc';
                if ($sort.selectedIndex) {
                    QueryString().set('sort', $sort.value);
                } else {
                    QueryString().delete('sort');
                }
            }
            pl.setPage(0);
            return true;
        },
    });

    pl.activate();
    pl.load();
}
